import { useRef, useState } from "react";
import { Crosshair, Grid3x3, Axis3d } from "lucide-react";
import { cn } from "@/lib/utils";
import { pointCalc, deviationLabel, describeDirection, fmtAbs, num } from "../../lib/calc";
import { COLORS } from "../../lib/constants";
import ChassisSvg, { mapGeometry } from "../map/ChassisSvg";
import { TraceCoordinateDialog } from "../map/TraceCoordinateDialog";
import { DrawToolbar } from "../draw/DrawToolbar";
import { ShapeView } from "../draw/ShapesLayer";
import { useDrawing } from "../draw/useDrawing";
import { StepTitle, Panel, ActionButton, SitBadge } from "../common/Field";

const TOOLS = ["select", "line", "hline", "vline", "dline", "arrow", "circle", "rect", "point", "text", "cota", "anomaly", "axisX", "axisY", "axisZ"];

export default function Step4Map({ insp, update }) {
  const [view, setView] = useState("top");
  const [tool, setTool] = useState("select");
  const [color, setColor] = useState(COLORS.dev);
  const [width, setWidth] = useState(4);
  const [label, setLabel] = useState("");
  const [selected, setSelected] = useState(null);
  const [traceOpen, setTraceOpen] = useState(false);
  const svgRef = useRef(null);
  const drawings = insp.drawings || {};
  const k = num(drawings.escala_desvio) || 20;
  const setDraw = (patch) => update("drawings", (d) => ({ ...(d || {}), ...(typeof patch === "function" ? patch(d || {}) : patch) }));
  const setShapes = (fn) => setDraw((d) => ({ [view]: fn(d[view] || []) }));

  const onAdd = (shape) => {
    let s = shape;
    if (s.type === "cota" && !s.text) {
      const g = mapGeometry(insp, view);
      s = { ...s, text: `${Math.round(Math.hypot(s.x2 - s.x1, s.y2 - s.y1) / g.s)} mm` };
    }
    setShapes((list) => [...list, s]);
  };
  const { draft, handlers } = useDrawing({ svgRef, tool, color, width, label, onAdd });
  const rows = (insp.points || []).map((p) => ({ p, c: pointCalc(p, insp.reference || {}) }));

  return (
    <div className="flex flex-col gap-6">
      <StepTitle n={4} title="Mapa de desalinhamento" subtitle="Coordenadas encontradas sobrepostas ao desenho técnico do chassi. Linha azul = referência · linha vermelha com seta = desvio encontrado, com valor em mm e identificação do ponto."
        actions={<ActionButton icon={Crosshair} onClick={() => setTraceOpen(true)} testId="btn-tracar-coordenada">Traçar coordenada</ActionButton>} />

      <Panel testId="panel-map" title="Editor gráfico" subtitle="Selecione uma ferramenta e desenhe sobre a vista. No modo Selecionar, clique numa anotação para excluí-la."
        actions={
          <div className="flex border border-[#1E2F45] rounded-sm p-0.5">
            {[["top", "Vista superior"], ["side", "Vista lateral"]].map(([v, l]) => (
              <button key={v} type="button" data-testid={`map-view-${v}`} onClick={() => { setView(v); setSelected(null); }}
                className={cn("h-8 px-3 rounded-sm font-display font-bold uppercase tracking-wider text-sm", view === v ? "bg-[#FFD500] text-[#050B10]" : "text-slate-400 hover:text-white")}>{l}</button>
            ))}
          </div>
        }>
        <DrawToolbar tools={TOOLS} tool={tool} setTool={(t) => { setTool(t); setSelected(null); }} color={color} setColor={setColor} width={width} setWidth={setWidth} label={label} setLabel={setLabel}
          onUndo={() => setShapes((l) => l.slice(0, -1))} canDelete={!!selected}
          onDelete={() => { setShapes((l) => l.filter((s) => s.id !== selected)); setSelected(null); }}
          onClear={() => { if (window.confirm("Remover todas as anotações desta vista?")) setShapes(() => []); }} prefix="map" />
        <div className="flex flex-wrap items-center gap-4 mt-4 mb-3">
          <button type="button" data-testid="map-toggle-grid" onClick={() => setDraw({ grade: drawings.grade === false })} className={cn("h-8 px-3 flex items-center gap-2 rounded-sm border font-mono text-xs", drawings.grade !== false ? "border-[#FFD500]/60 text-[#FFD500]" : "border-[#1E2F45] text-slate-400")}><Grid3x3 size={14} /> GRADE DE COORDENADAS</button>
          <button type="button" data-testid="map-toggle-axes" onClick={() => setDraw({ eixos: drawings.eixos === false })} className={cn("h-8 px-3 flex items-center gap-2 rounded-sm border font-mono text-xs", drawings.eixos !== false ? "border-[#FFD500]/60 text-[#FFD500]" : "border-[#1E2F45] text-slate-400")}><Axis3d size={14} /> EIXOS X·Y·Z</button>
          <label className="flex items-center gap-3 font-mono text-xs text-slate-400">
            AMPLIAÇÃO DOS VETORES
            <input type="range" min={1} max={100} value={k} onChange={(e) => setDraw({ escala_desvio: Number(e.target.value) })} data-testid="map-scale-slider" className="accent-[#FFD500] w-40" />
            <span className="text-[#FFD500] font-bold w-10" data-testid="map-scale-value">×{k}</span>
          </label>
        </div>
        <div className={cn("border border-[#1E2F45] rounded-sm overflow-hidden", tool !== "select" && "cursor-crosshair")}>
          <ChassisSvg insp={insp} view={view} svgRef={svgRef} svgProps={handlers} testId="mapa-desalinhamento-svg-canvas"
            selectedShapeId={selected} onSelectShape={tool === "select" ? setSelected : undefined}>
            {draft && <g opacity={0.8}><ShapeView s={draft} /></g>}
          </ChassisSvg>
        </div>
      </Panel>

      <Panel title="Vetores de desvio" subtitle="Referência original → coordenada medida → cálculo do desvio" testId="panel-vectors">
        {!rows.length && <p className="text-sm text-slate-500">Nenhum ponto medido. Use TRAÇAR COORDENADA ou a tela 3.</p>}
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
          {rows.map(({ p, c }) => {
            const l = deviationLabel(p.numero, c);
            return (
              <div key={p.id} className="border border-[#1E2F45] rounded-sm p-3 bg-[#050B10]" data-testid={`vector-card-${p.numero}`}>
                <div className="flex justify-between items-start gap-2">
                  <div className="font-mono font-bold text-[#FF0000] text-sm">{l.text}</div>
                  <SitBadge situacao={c.situacao} />
                </div>
                <div className="grid grid-cols-2 gap-2 mt-3 font-mono text-[11px]">
                  <div><div className="text-[#7FB6EE]">REFERÊNCIA</div>X={p.ref?.x || "—"} / Y={p.ref?.y || "—"} / Z={p.ref?.z || "—"}</div>
                  <div><div className="text-white">MEDIDO</div>X={p.found?.x || "—"} / Y={p.found?.y || "—"} / Z={p.found?.z || "—"}</div>
                </div>
                <div className="mt-2 text-[11px] text-slate-400">{describeDirection(c.dx, c.dy, c.dz, insp.reference || {}) || "Sem desvio calculado"} · Resultante {fmtAbs(c.resultant)}</div>
              </div>
            );
          })}
        </div>
      </Panel>
      <TraceCoordinateDialog open={traceOpen} onOpenChange={setTraceOpen} insp={insp} update={update} />
    </div>
  );
}
